package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.PressableStyle
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.textureBrushed
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton

/**
 * A square icon-only control that reuses the Button surface language for round machine knobs.
 * Port of `components/forms/IconButton.jsx`. [label] is required — it drives both `aria-label`
 * and the native tooltip. Set [round] for a pill/knob shape.
 */
@Composable
fun IconButton(
  icon: String,
  label: String,
  modifier: Modifier = Modifier,
  variant: ButtonVariant = ButtonVariant.Secondary,
  size: ButtonSize = ButtonSize.Md,
  round: Boolean = false,
  disabled: Boolean = false,
  onClick: (() -> Unit)? = null,
  onContextMenu: (() -> Unit)? = null,
  onLongPress: (() -> Unit)? = null,
) {
  val box = when (size) {
    ButtonSize.Sm -> 2.cssRem
    ButtonSize.Md -> 2.75.cssRem
    ButtonSize.Lg -> 3.25.cssRem
  }
  val iconSize = when (size) {
    ButtonSize.Sm -> 16.px
    ButtonSize.Md -> 19.px
    ButtonSize.Lg -> 22.px
  }

  val clickHandler = onClick
  val longPressTimer = remember { arrayOfNulls<Int>(1) }
  val suppressNextClick = remember { booleanArrayOf(false) }
  val mod = PressableStyle.toModifier()
    .displayInlineFlex()
    .alignItems(AlignItems.Center)
    .justifyContent(JustifyContent.Center)
    .width(box)
    .height(box)
    .flex("0 0 auto")
    .borderRadius(if (round) DmEffects.radiusPill else DmEffects.radiusMd)
    .cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer)
    .opacity(if (disabled) 0.5 else 1.0)
    .iconButtonVariant(variant)
    // Uniform machined top-highlight + flat drop across all icon-button variants.
    .boxShadowRaw("inset 0 1px 0 rgba(232,240,247,.10), ${DmEffects.shadowFlat}")
    .then(modifier)

  DomButton(
    attrs = mod.toAttrs {
      attr("aria-label", label)
      attr("title", label)
      if (disabled) attr("disabled", "")
      if (clickHandler != null) onClick {
        if (suppressNextClick[0]) suppressNextClick[0] = false else clickHandler()
      }
      if (!disabled && onContextMenu != null) onContextMenu { event ->
        event.preventDefault()
        onContextMenu()
      }
      if (!disabled && onLongPress != null) {
        onTouchStart {
          longPressTimer[0]?.let(window::clearTimeout)
          longPressTimer[0] = window.setTimeout({
            suppressNextClick[0] = true
            window.setTimeout({ suppressNextClick[0] = false }, 1000)
            onLongPress()
            longPressTimer[0] = null
          }, 600)
        }
        onTouchMove {
          longPressTimer[0]?.let(window::clearTimeout)
          longPressTimer[0] = null
        }
        onTouchEnd {
          longPressTimer[0]?.let(window::clearTimeout)
          longPressTimer[0] = null
        }
        onTouchCancel {
          longPressTimer[0]?.let(window::clearTimeout)
          longPressTimer[0] = null
        }
      }
    }
  ) {
    Icon(icon, IconWeight.Bold, Modifier.fontSize(iconSize))
  }
}

/** IconButton variant surface — background/texture/text/border only (shadow is applied uniformly). */
private fun Modifier.iconButtonVariant(variant: ButtonVariant): Modifier = when (variant) {
  ButtonVariant.Primary -> this
    .backgroundColor(DmColor.brass500).textureBrushed().color(DmColor.onPrimary)
    .border(1.px, LineStyle.Solid, DmColor.brass600)

  ButtonVariant.Secondary -> this
    .backgroundColor(DmColor.surfaceRaised).color(DmColor.text)
    .border(1.px, LineStyle.Solid, DmColor.borderStrong)

  ButtonVariant.Ghost -> this
    .backgroundColor(Colors.Transparent).color(DmColor.textSecondary)
    .border(1.px, LineStyle.Solid, Colors.Transparent)

  ButtonVariant.Danger -> this
    .backgroundColor(DmColor.rust500).textureBrushed().color(DmColor.steel50)
    .border(1.px, LineStyle.Solid, DmColor.rust600)
}
